import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import {
  useActiveLanguageIsUnseeded,
  useLanguageCode,
} from "../lib/activeLanguage";
import type { LanguageCode } from "../lib/languages";
import { requireAuth } from "../lib/requireAuth";
import { UnseededLanguageLanding } from "../lib/UnseededLanguageLanding";
import { FilterSelect } from "../components/FilterSelect";
import { Pagination, PageMeta } from "../components/Pagination";
import { PageEyebrow } from "../components/PageEyebrow";
import { Centered } from "../components/Centered";

const PAGE_SIZE = 25;

export const Route = createFileRoute("/lexicon")({
  beforeLoad: ({ context, location }) =>
    requireAuth(context.queryClient, location.href),
  component: LexiconPage,
});

type Gloss = {
  target_lang: string;
  gloss: string;
  gloss_type: string;
};

type CefrTag = {
  source: string;
  level: string;
};

type Lexeme = {
  id: number;
  language: string;
  lemma: string;
  pos: string;
  meta: Record<string, unknown>;
  glosses: Gloss[];
  fieldSlugs: string[];
  cefrTags: CefrTag[];
};

type LexemePage = {
  items: Lexeme[];
  total: number;
  limit: number;
  offset: number;
};

async function fetchLexemes(query: {
  language: LanguageCode;
  field?: string;
  pos?: string;
}): Promise<LexemePage> {
  const { data, error } = await api.GET("/lexemes", {
    params: {
      query: {
        language: query.language,
        limit: 200,
        ...(query.field ? { field: query.field } : {}),
        ...(query.pos ? { pos: query.pos } : {}),
      },
    },
  });
  if (error) throw error;
  return data as unknown as LexemePage;
}

// Field slugs aren't server-listed separately yet — derive from the loaded set.
function collectFields(items: Lexeme[]): string[] {
  const s = new Set<string>();
  for (const l of items) for (const f of l.fieldSlugs) s.add(f);
  return [...s].sort();
}

function collectPos(items: Lexeme[]): string[] {
  const s = new Set<string>();
  for (const l of items) s.add(l.pos);
  return [...s].sort();
}

function LexiconPage() {
  const language = useLanguageCode();
  const unseeded = useActiveLanguageIsUnseeded();
  const [field, setField] = useState<string>("");
  const [pos, setPos] = useState<string>("");
  const [page, setPage] = useState<number>(1);

  // Filter changes invalidate the current page — jump back to page 1
  // so the user isn't staring at "Page 4 of 1".
  useEffect(() => {
    setPage(1);
  }, [field, pos, language]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["lexicon", language, field, pos],
    queryFn: () => fetchLexemes({ language, field, pos }),
    // Lexicon entries don't change during a session — 5 min is fine.
    staleTime: 5 * 60_000,
    enabled: !unseeded,
  });

  if (unseeded) return <UnseededLanguageLanding />;
  if (isLoading) return <Centered width="wide">Loading lexicon…</Centered>;
  if (isError || !data)
    return <Centered width="wide">Could not load lexicon.</Centered>;
  if (data.total === 0) {
    return (
      <Centered width="wide">
        <p className="text-ink-700 mb-2">No lexemes seeded yet.</p>
        <p className="text-ink-400 text-sm">
          Run{" "}
          <code className="bg-ink-100 px-1 rounded">
            uv run python -m lingua.seed.full
          </code>{" "}
          to populate the lexicon.
        </p>
      </Centered>
    );
  }

  // For the filter dropdowns, we fetch once with no filters and reuse.
  // Simpler: collect from the current page. Acceptable while the lexicon has
  // ~50 entries; revisit if seeding scales to 700+.
  const fieldOptions = collectFields(data.items);
  const posOptions = collectPos(data.items);

  // Pagination is client-side over the already-filtered set so the filter
  // option derivation above still sees the whole page. Once the seed scales
  // past ~200, switch to server-side limit/offset (the API already supports
  // it) and pull filter options from a separate unfiltered query.
  const totalShown = data.items.length;
  const totalPages = Math.max(1, Math.ceil(totalShown / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const pageItems = data.items.slice(startIdx, startIdx + PAGE_SIZE);
  const showFrom = totalShown === 0 ? 0 : startIdx + 1;
  const showTo = Math.min(startIdx + PAGE_SIZE, totalShown);

  return (
    <section className="mx-auto max-w-4xl px-4 sm:px-6 py-8 sm:py-12">
      <PageEyebrow
        label="Reference"
        mono={`${language}.lexicon`}
        count={`${data.total} ${data.total === 1 ? "entry" : "entries"}`}
      />
      <h2 className="font-serif text-3xl text-ink-900 mb-2 tracking-tight">
        Lexicon
      </h2>
      <p className="text-ink-500 text-sm mb-5 sm:mb-6 max-w-2xl">
        The vocabulary side of the app — each entry has its part-of-speech,
        English and Spanish glosses, and a topic tag. Separate from grammar
        skills.
      </p>
      <div className="hairline mb-6" />

      <div className="flex flex-wrap gap-3 mb-6">
        <FilterSelect
          layout="inline"
          label="Field"
          value={field}
          onChange={setField}
          options={fieldOptions}
        />
        <FilterSelect
          layout="inline"
          label="POS"
          value={pos}
          onChange={setPos}
          options={posOptions}
        />
      </div>

      {totalShown > 0 && totalPages > 1 && (
        <PageMeta
          from={showFrom}
          to={showTo}
          total={totalShown}
          noun={totalShown === 1 ? "entry" : "entries"}
        />
      )}

      <Pagination
        page={safePage}
        totalPages={totalPages}
        onPrev={() => setPage(Math.max(1, safePage - 1))}
        onNext={() => setPage(Math.min(totalPages, safePage + 1))}
        ariaLabel="Lexicon pagination (top)"
        className="mb-3"
      />

      <ul className="divide-y divide-ink-100 border border-ink-100 bg-paper">
        {pageItems.map((lx) => (
          <LexemeCard key={lx.id} lexeme={lx} />
        ))}
      </ul>

      <Pagination
        page={safePage}
        totalPages={totalPages}
        onPrev={() => setPage(Math.max(1, safePage - 1))}
        onNext={() => setPage(Math.min(totalPages, safePage + 1))}
        ariaLabel="Lexicon pagination"
      />
    </section>
  );
}

function LexemeCard({ lexeme }: { lexeme: Lexeme }) {
  const gender = lexeme.meta.Gender as string | undefined;
  const article =
    gender === "Masc"
      ? "der "
      : gender === "Fem"
        ? "die "
        : gender === "Neut"
          ? "das "
          : "";

  return (
    <li className="p-3 sm:p-4 hover:bg-ink-50 transition-colors">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-2">
        <div className="min-w-0">
          <span className="font-serif text-lg sm:text-xl text-ink-900">
            {article}
            {lexeme.lemma}
          </span>
          {/* Part-of-speech reads as a citation/address — Plex Mono,
              ink-soft, no chrome — same treatment as skill codes (§10). */}
          <span className="ml-2 skill-code text-[11px] uppercase">
            {lexeme.pos}
          </span>
        </div>
        <div className="text-xs flex gap-2 flex-wrap sm:justify-end items-center">
          {/* CEFR level is a category address, not a state — §7 reserves
              emerald for "mastered". Render as mono citation instead. */}
          {(lexeme.cefrTags ?? []).map((t) => (
            <span
              key={`${t.source}-${t.level}`}
              className="font-mono tabular-nums text-ink-500"
              title={`${t.source} curriculum`}
            >
              {t.level}
            </span>
          ))}
          {lexeme.fieldSlugs.map((f) => (
            <span
              key={f}
              className="border border-ink-200 px-1.5 py-0.5 text-ink-500 text-[11px]"
            >
              {f}
            </span>
          ))}
        </div>
      </div>

      <div className="text-sm text-ink-700 space-y-0.5">
        {lexeme.glosses.map((g, i) => (
          <div key={i} className="flex gap-2">
            <span className="font-mono text-ink-400 text-[11px] uppercase tracking-wide w-6 pt-0.5">
              {g.target_lang}
            </span>
            <span>{g.gloss}</span>
          </div>
        ))}
      </div>

      {typeof lexeme.meta.note === "string" && lexeme.meta.note && (
        <div className="text-xs text-ink-400 italic mt-2 pl-3 border-l border-ink-200">
          {lexeme.meta.note as string}
        </div>
      )}
    </li>
  );
}
